import { useEffect, useState, useCallback } from "react";

import axios from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";

import { normalizeEvent } from "./normalizeEvent";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DeploymentEvent {
  id: string;
  sagaId: string;
  eventType: string;
  systemId: number;
  systemAlias: string;
  organizationId: number;
  step: string; // "vcs" | "build" | "deploy"
  status: string; // "pending" | "in_progress" | "completed" | "failed"
  severity: string; // "info" | "warning" | "error"
  message: string;
  metadata?: Record<string, unknown>;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  /** When the step actually started (from pipeline_state), if available */
  startedAt?: string | null;
  /** When the step actually completed (from pipeline_state), if available */
  completedAt?: string | null;
}

export interface DeploymentEventsResponse {
  events: DeploymentEvent[];
  total: number;
}

export interface UseDeploymentEventsResult {
  events: DeploymentEvent[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch deployment pipeline events for a system.
 *
 * Used to show VCS provisioning → Build → Deploy progress in the UI.
 *
 * Endpoints:
 * - GET /api/v1/deployment-events?systemId=<id>
 * - GET /api/v1/deployment-events/system/:alias
 */
export function useDeploymentEvents(
  alias: string | undefined,
): UseDeploymentEventsResult {
  const [events, setEvents] = useState<DeploymentEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = () => orgSessionManager.getToken() ?? "";

  const fetchEvents = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}/api/v1/deployment-events/system/${alias}`;
      const res = await axios.get<DeploymentEventsResponse>(url, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setEvents(res.data.events.map(normalizeEvent));
    } catch {
      // Endpoint might not exist yet - that's OK
      setError("Deployment events not available");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    if (!alias) return;
    void fetchEvents();
  }, [alias, fetchEvents]);

  return { events, loading, error, refetch: fetchEvents };
}

/**
 * Get the latest status for each step in the deployment pipeline.
 * Useful for showing progress bars.
 */
export function usePipelineStatus(alias: string | undefined) {
  const { events, loading, error } = useDeploymentEvents(alias);

  const _steps = ["vcs", "build", "deploy"] as const;
  type Step = (typeof _steps)[number];

  const statusByStep: Record<Step, { status: string; message: string } | null> =
    {
      vcs: null,
      build: null,
      deploy: null,
    };

  // Find the latest event for each step
  for (const event of events) {
    if (event.step === "vcs" && !statusByStep.vcs) {
      statusByStep.vcs = { status: event.status, message: event.message };
    }
    if (event.step === "build" && !statusByStep.build) {
      statusByStep.build = { status: event.status, message: event.message };
    }
    if (event.step === "deploy" && !statusByStep.deploy) {
      statusByStep.deploy = { status: event.status, message: event.message };
    }
  }

  // Determine overall pipeline status
  const pipelineStatus = (() => {
    if (statusByStep.deploy?.status === "completed") return "completed";
    if (statusByStep.deploy?.status === "in_progress") return "deploying";
    if (statusByStep.build?.status === "in_progress") return "building";
    if (statusByStep.vcs?.status === "in_progress") return "provisioning";
    if (events.length === 0) return "idle";
    if (
      statusByStep.vcs?.status === "failed" ||
      statusByStep.build?.status === "failed" ||
      statusByStep.deploy?.status === "failed"
    )
      return "failed";
    return "idle";
  })();

  return {
    steps: statusByStep,
    pipelineStatus,
    loading,
    error,
  };
}
